import { Link } from 'wouter';
import { 
  Instagram, 
  Facebook, 
  Twitter, 
  Youtube, 
  Mail, 
  Phone,
  MapPin,
  Heart,
  Star,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import BearFluencerBadge from './BearFluencerBadge';

export default function InfluencerFooter() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-purple-50 to-pink-50 border-t border-purple-200 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Sparkles className="h-6 w-6 text-purple-600" />
              <h3 className="text-lg font-bold text-purple-800">BearFluencer Program</h3>
            </div>
            <p className="text-sm text-gray-600">
              Join our exclusive community of storytellers and earn while sharing the magic of personalized bedtime stories.
            </p>
            <BearFluencerBadge size="medium" showTooltip={true} variant="prominent" />
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-3">Influencer Resources</h4>
            <ul className="space-y-2">
              <li>
                <Link href="/influencer/dashboard" className="text-sm text-gray-600 hover:text-purple-600 transition-colors">
                  <span className="flex items-center gap-1">
                    Dashboard
                    <Star className="h-3 w-3" />
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/influencer/resources" className="text-sm text-gray-600 hover:text-purple-600 transition-colors">
                  Brand Resources
                </Link>
              </li>
              <li>
                <Link href="/influencer/application" className="text-sm text-gray-600 hover:text-purple-600 transition-colors">
                  Apply Now
                </Link>
              </li>
              <li>
                <a 
                  href="https://ftc.gov/influencer-guidelines" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-gray-600 hover:text-purple-600 transition-colors flex items-center gap-1"
                >
                  FTC Guidelines
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-3">Support & Help</h4>
            <ul className="space-y-2">
              <li>
                <a 
                  href="mailto:influencer@snugglecrafters.com" 
                  className="text-sm text-gray-600 hover:text-purple-600 transition-colors flex items-center gap-1"
                >
                  <Mail className="h-3 w-3" />
                  Influencer Support
                </a>
              </li>
              <li>
                <Link href="/help" className="text-sm text-gray-600 hover:text-purple-600 transition-colors">
                  Help Center
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-sm text-gray-600 hover:text-purple-600 transition-colors">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link href="/partnerships-contact" className="text-sm text-gray-600 hover:text-purple-600 transition-colors">
                  Partnership Inquiries
                </Link>
              </li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-3">Connect With Us</h4>
            <div className="flex space-x-3 mb-4">
              <a 
                href="https://instagram.com/snugglecrafters" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-purple-100 p-2 rounded-lg hover:bg-purple-200 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5 text-purple-600" />
              </a>
              <a 
                href="https://facebook.com/snugglecrafters" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-purple-100 p-2 rounded-lg hover:bg-purple-200 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5 text-purple-600" />
              </a>
              <a 
                href="https://twitter.com/snugglecrafters" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-purple-100 p-2 rounded-lg hover:bg-purple-200 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5 text-purple-600" />
              </a>
              <a 
                href="https://youtube.com/snugglecrafters" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-purple-100 p-2 rounded-lg hover:bg-purple-200 transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5 text-purple-600" />
              </a>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <p className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                San Francisco, CA
              </p>
              <p className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                1-800-SNUGGLE
              </p>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-purple-200" />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <p>© {currentYear} SnuggleCrafters</p>
            <Link href="/terms" className="hover:text-purple-600 transition-colors">
              Terms
            </Link>
            <Link href="/privacy" className="hover:text-purple-600 transition-colors">
              Privacy
            </Link>
            <Link href="/legal/influencer-agreement" className="hover:text-purple-600 transition-colors">
              Influencer Agreement
            </Link>
          </div>

          <div className="flex items-center text-sm text-gray-600">
            <span>Made with</span>
            <Heart className="h-4 w-4 mx-1 text-pink-500 fill-current" />
            <span>by the SnuggleCrafters Team</span>
          </div>
        </div>
      </div>
    </footer>
  );
}